import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, RadioGroup } from '@parrot-software/design-system';
import { ChevronRight, Loader2, Plus } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { toast } from 'sonner';

import type { PaymentMethodViewDto, StripeSessionDto } from '@subscriptions/application/dtos';
import { AnalyticsStatuses, TrackedPaymentMethods, WizardSteps } from '@subscriptions/presentation/constants';
import { WizardStepFooter } from '@subscriptions/presentation/components/base';
import { resolveSessionMode } from '@subscriptions/presentation/components/specific/BlockCreationWizard/sessionMode';
import { resolveWizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import {
  useAttachBlockPaymentMethod,
  useGenerateBlockPaymentSession,
  useSubscriptionAnalytics,
  useSubscriptionBlock,
} from '@subscriptions/presentation/hooks';
import { resolveSubscriptionErrorMessage } from '@subscriptions/presentation/utils';

import { SavedCardRadioItem } from './SavedCardRadioItem';

/**
 * Route state for the saved-cards step: the org's saved ACTIVE cards, pushed by
 * the fiscal-data step (same ephemeral idiom as `CheckoutRouteState`).
 */
export type CardsRouteState = {
  cards: PaymentMethodViewDto[];
};

// Centered layout: the container locks the viewport height and centers the block
// on both axes; the block sizes to its cards and, when they exceed the available
// height, the bordered card shrinks (min-h-0 chain) and scrolls internally while
// the title/footer stay fixed outside the border.
type BlockCreationStepCardsProps = {
  blockUuid: string;
  cards: PaymentMethodViewDto[];
  onAttached: (paymentMethod: PaymentMethodViewDto) => void;
  onCardSessionReady: (session: StripeSessionDto) => void;
};

/**
 * Saved-cards selection step of the block creation wizard (step 3.5, CARD with
 * active license only — Figma 3002-121761).
 *
 * Product overrides vs the Figma: brand capitalized via `formatBrand` (`Visa`,
 * not `VISA`) and no holder name. The card already paying the block arrives
 * pre-selected (re-attaching it is idempotent); an explicit user selection
 * always takes precedence. An attach failure keeps the user on the step.
 *
 * @param props - Component properties
 * @returns React element rendering the step
 */
export const BlockCreationStepCards = ({
  blockUuid,
  cards,
  onAttached,
  onCardSessionReady,
}: BlockCreationStepCardsProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const { data: block } = useSubscriptionBlock(blockUuid);
  const { mutateAsync: attachPaymentMethod, isLoading: isAttaching } = useAttachBlockPaymentMethod();
  const { search } = useLocation();
  const { mutateAsync: generateSession, isLoading: isGeneratingSession } = useGenerateBlockPaymentSession();
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>(undefined);
  const { trackPaymentMethodSaved, trackPaymentStepViewed } = useSubscriptionAnalytics();
  const origin = resolveWizardOrigin(search);

  const isUpdate = Boolean(block?.paymentMethod?.uuid);
  const isBusy = isAttaching || isGeneratingSession;

  useEffect(() => {
    trackPaymentStepViewed({
      blockUuid,
      origin,
      paymentMethod: TrackedPaymentMethods.CARD,
      step: WizardSteps.CARDS,
    });
  }, [blockUuid, origin, trackPaymentStepViewed]);

  useEffect(() => {
    const currentCardUuid = block?.paymentMethod?.uuid;
    if (!currentCardUuid || !cards.some((card) => card.uuid === currentCardUuid)) return;
    setSelectedUuid((previous) => previous ?? currentCardUuid);
  }, [block, cards]);

  const handleAddNewCard = async () => {
    try {
      // The step is reached with an active license, but that is not enough to fix the mode: a
      // churned block can read as AVAILABLE and land here, and hardcoding the setup-and-charge
      // mode would bill it a card-verification hold instead of the reactivation charge.
      const session = await generateSession({
        blockUuid,
        isUpdate,
        mode: resolveSessionMode(true, search),
      });
      onCardSessionReady(session);
    } catch (error) {
      toast.error(resolveSubscriptionErrorMessage(error, t, i18n, 'blockCreationWizard.errors.paymentSessionFailed'));
    }
  };

  const handleContinue = async () => {
    if (!selectedUuid) return;

    try {
      const attached = await attachPaymentMethod({ blockUuid, paymentMethodUuid: selectedUuid });
      trackPaymentMethodSaved({
        blockUuid,
        origin,
        paymentMethod: TrackedPaymentMethods.CARD,
        status: AnalyticsStatuses.SUCCESS,
      });
      onAttached(attached);
    } catch (error) {
      trackPaymentMethodSaved({
        blockUuid,
        origin,
        paymentMethod: TrackedPaymentMethods.CARD,
        status: AnalyticsStatuses.FAILED,
      });
      toast.error(resolveSubscriptionErrorMessage(error, t, i18n, 'blockCreationWizard.cards.attachError'));
    }
  };

  return (
    <div
      className={cn(
        'flex h-[100dvh] flex-col items-center justify-center overflow-hidden font-sans',
        'px-4 pt-20 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
        'md:px-6 md:pb-12 md:pt-20',
      )}
    >
      <div className="flex min-h-0 w-full max-w-[640px] flex-col gap-6">
        <h1 className="shrink-0 text-xl font-semibold leading-8 text-foreground">
          {t('blockCreationWizard.cards.title')}
        </h1>

        <div
          className={cn(
            'flex min-h-[300px] flex-col gap-4 overflow-y-auto',
            'rounded-xl border bg-background p-4 shadow-sm md:p-6',
          )}
        >
          <RadioGroup className="flex flex-col gap-4" onValueChange={setSelectedUuid} value={selectedUuid ?? ''}>
            {cards.map((card) => (
              <SavedCardRadioItem card={card} isSelected={selectedUuid === card.uuid} key={card.uuid} />
            ))}
          </RadioGroup>

          <Button
            className="w-full shrink-0"
            disabled={isBusy}
            onClick={handleAddNewCard}
            type="button"
            variant="outline"
          >
            {isGeneratingSession ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            {t('blockCreationWizard.cards.addAnotherCard')}
          </Button>
        </div>

        <WizardStepFooter className="shrink-0">
          <Button
            className="w-full md:w-auto"
            disabled={!selectedUuid || isBusy}
            onClick={handleContinue}
            type="button"
          >
            {t('blockCreationWizard.cards.submit')}
            {isAttaching ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronRight className="ml-2 h-4 w-4" />
            )}
          </Button>
        </WizardStepFooter>
      </div>
    </div>
  );
};
